import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { IncidentFormModal } from '../features/iso27001/incidents/IncidentFormModal';
import {
  createInfoSecurityIncident,
  escalateInfoSecurityIncident,
  fetchInfoSecurityIncidents,
  transitionInfoSecurityIncidentStatus,
  updateInfoSecurityIncident,
} from '../features/iso27001/incidents/incidentsApi';
import type { InfoSecurityIncidentSummary } from '../features/iso27001/incidents/types';
import {
  INCIDENT_TYPE_LABEL,
  SEVERITY_LABEL,
  STATUS_LABEL,
  severityBadgeClass,
  statusBadgeClass,
} from '../features/iso27001/incidents/incidentsUtils';

const NEXT_STATUS: Partial<
  Record<InfoSecurityIncidentSummary['status'], InfoSecurityIncidentSummary['status']>
> = {
  abierto: 'en_investigacion',
  en_investigacion: 'contenido',
  contenido: 'cerrado',
};

export function InfoSecurityIncidentsPage() {
  const { canEdit } = usePlatformAccessLevel('iso27001.incidents.access');
  const [items, setItems] = useState<InfoSecurityIncidentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<InfoSecurityIncidentSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchInfoSecurityIncidents({ page, pageSize: 20 });
      setItems(list.data);
      setTotalPages(list.pagination.pages);
    } catch {
      setError('No se pudieron cargar los incidentes de seguridad.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const advanceStatus = async (item: InfoSecurityIncidentSummary) => {
    const next = NEXT_STATUS[item.status];
    if (!next) return;
    await transitionInfoSecurityIncidentStatus(item.id, next);
    await load();
  };

  const escalate = async (item: InfoSecurityIncidentSummary) => {
    await escalateInfoSecurityIncident(item.id);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Incidentes de seguridad de la información</h1>
          <p className="text-muted-foreground text-sm">ISO 27001 — gestión de incidentes SGSI</p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            Registrar incidente
          </button>
        )}
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Código</th>
              <th className="px-3 py-2 text-left">Detectado</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Severidad</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Activos afectados</th>
              {canEdit && <th className="px-3 py-2 text-left">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={canEdit ? 7 : 6}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={canEdit ? 7 : 6}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  Sin incidentes registrados.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono">{item.code}</td>
                  <td className="px-3 py-2">{new Date(item.detectedAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2">{INCIDENT_TYPE_LABEL[item.type]}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs ${severityBadgeClass(item.severity)}`}
                    >
                      {SEVERITY_LABEL[item.severity]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs ${statusBadgeClass(item.status)}`}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 max-w-xs truncate" title={item.affectedAssets ?? ''}>
                    {item.affectedAssets || '—'}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 space-x-1">
                      <button
                        type="button"
                        className="btn btn-outline btn-xs"
                        onClick={() => setEditItem(item)}
                      >
                        Investigar
                      </button>
                      {NEXT_STATUS[item.status] && (
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => void advanceStatus(item)}
                        >
                          {STATUS_LABEL[NEXT_STATUS[item.status]!]}
                        </button>
                      )}
                      {!item.bridgeRecordId && item.status !== 'cerrado' && (
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => void escalate(item)}
                        >
                          Escalar CAPA
                        </button>
                      )}
                      {item.bridgeRecordId && (
                        <Link
                          className="text-primary text-xs underline"
                          to={`/registros/no-conformidades/${item.bridgeRecordId}`}
                        >
                          Ver NC
                        </Link>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <IncidentFormModal
        open={createOpen}
        title="Registrar incidente de seguridad"
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload) => {
          await createInfoSecurityIncident(payload);
          await load();
        }}
      />

      <IncidentFormModal
        open={Boolean(editItem)}
        title={editItem ? `Investigar ${editItem.code}` : 'Investigar'}
        showInvestigation
        initial={editItem ?? undefined}
        onClose={() => setEditItem(null)}
        onSubmit={async (payload) => {
          if (!editItem) return;
          await updateInfoSecurityIncident(editItem.id, payload);
          await load();
        }}
      />
    </div>
  );
}
